<template>
  <div class="calendar-details">
    <b-card class="shadow-sm">
      <!-- Header with back button -->
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0"
          style="margin-left: 10px;"
          >
            <feather-icon icon="CalendarIcon" class="mr-10" />
            Lesson Plan Details
          </h5>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="$emit('close')"
            class="back-button pl-10"
            style="margin-left: 10px;"
          >
            <feather-icon icon="ArrowLeftIcon" class="mr-2" />
            Back to Calendar
          </b-button>
        </div>
      </template>

      <!-- Main content -->
      <div v-if="plannerData" class="planner-details">
        <!-- Basic Info Section -->
        <div class="basic-info mb-4">
          <b-row>
            <b-col md="6">
              <h6 class="section-title">Subject Information</h6>
              <div class="info-line">
                <strong>Topic:</strong> {{ topicName || 'Unknown' }}
              </div>
              <div class="info-line">
                <strong>Subtopic:</strong> {{ subtopicName || 'Unknown' }}
              </div>
            </b-col>
            <b-col md="6">
              <h6 class="section-title">Schedule Information</h6>
              <div class="info-line">
                <strong>Date Range:</strong> {{ formatDate(plannerData.dtFrom) }} to {{ formatDate(plannerData.dtTo) }}
              </div>
              <div v-if="plannerData.dayFromName && plannerData.dayToName" class="info-line">
                <strong>Days:</strong> {{ plannerData.dayFromName }} to {{ plannerData.dayToName }}
              </div>
              <div v-if="plannerData.duration" class="info-line">
                <strong>Duration:</strong> {{ plannerData.duration }} hours
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Lesson Activities Section -->
        <div class="activities-section mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="section-title mb-0">
              Lesson Planner
              <span class="activity-count">({{ plannerData.plannerDayWiseDetails ? plannerData.plannerDayWiseDetails.length : 0 }} Planners)</span>
            </h6>
            <div class="action-buttons">
              <b-button
                v-if="plannerData.sow || (plannerData.plannerDayWise && plannerData.plannerDayWise.sow)"
                variant="outline-success"
                size="sm"
                @click="previewSow"
                class="mr-2"
              >
                <feather-icon icon="FileTextIcon" class="mr-1" />
                Preview SOW
              </b-button>
              <b-button
                v-if="plannerData.urdu || (plannerData.plannerDayWise && plannerData.plannerDayWise.urdu)"
                variant="outline-success"
                size="sm"
                @click="previewUrdu"
                class="mr-2"
              >
                <feather-icon icon="FileTextIcon" class="mr-1" />
                Preview Urdu
              </b-button>
              <b-button
                v-if="plannerData.plannerDayWiseDetails && plannerData.plannerDayWiseDetails.length > 0 && !shouldHideDetails"
                variant="outline-primary"
                size="sm"
                @click="viewAllPlannerDetails"
                class="view-details-btn"
              >
                <feather-icon icon="EyeIcon" class="mr-1" />
                View Details
              </b-button>
            </div>
          </div>

          <div v-if="plannerData.plannerDayWiseDetails && plannerData.plannerDayWiseDetails.length > 0 && !shouldHideDetails">
            <div
              v-for="(detail, index) in plannerData.plannerDayWiseDetails"
              :key="index"
              class="activity-item mb-3"
            >
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="activity-title">{{ getPlannerHeading(index) }}</h6>
              </div>
              <div v-if="detail.lessonOrganisation" class="info-line">
                <strong>Lesson Organisation:</strong> 
                <span v-if="hasUrls(detail.lessonOrganisation)" 
                      v-html="convertUrlsToLinks(detail.lessonOrganisation)" 
                      @click="handleLinkClick"></span>
                <span v-else>{{ detail.lessonOrganisation }}</span>
              </div>
              <div v-if="detail.activity" class="info-line">
                <strong>Activity:</strong> 
                <span v-if="hasUrls(detail.activity)" 
                      v-html="convertUrlsToLinks(detail.activity)" 
                      @click="handleLinkClick"></span>
                <span v-else>{{ detail.activity }}</span>
              </div>
              <div v-if="detail.resource" class="info-line">
                <strong>Resource:</strong> 
                <span v-if="hasUrls(detail.resource)" 
                      v-html="convertUrlsToLinks(detail.resource)" 
                      @click="handleLinkClick"></span>
                <span v-else>{{ detail.resource }}</span>
              </div>
              <div v-if="detail.assessmentStrategy" class="info-line">
                <strong>Assessment Strategy:</strong> 
                <span v-if="hasUrls(detail.assessmentStrategy)" 
                      v-html="convertUrlsToLinks(detail.assessmentStrategy)" 
                      @click="handleLinkClick"></span>
                <span v-else>{{ detail.assessmentStrategy }}</span>
              </div>
            </div>
          </div>

          <div v-else-if="shouldHideDetails" class="no-activities">
            <p class="text-muted">Lesson planner details are hidden for this plan.</p>
          </div>
          <div v-else class="no-activities">
            <p class="text-muted">No lesson planners found for this plan.</p>
          </div>
        </div>

        <!-- Completion and Notes Section -->
        <div class="completion-section">
          <h6 class="section-title">Lesson Completion</h6>
          
          <!-- Completion Checkbox -->
          <div class="completion-checkbox mb-3" :class="{ 'out-of-range': isCompleted && isLateCompletion }">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                id="completionCheckbox"
                v-model="isCompleted"
                @change="onCompletionChange"
              >
              <label class="custom-control-label" for="completionCheckbox">
                Mark as Completed
              </label>
            </div>
            
            <!-- Completion Status Message - Only show when there are unsaved changes -->
            <div v-if="completionStatusMessage && hasUnsavedChanges" class="completion-status-message mt-2">
              <div v-if="isCompletionInRange || (!isLateCompletion && !isCompletionInRange)" class="text-success">
                <i class="feather-icon" data-feather="check-circle"></i>
                {{ completionStatusMessage }}
              </div>
              <div v-else-if="isLateCompletion" class="text-danger">
                <i class="feather-icon" data-feather="alert-triangle"></i>
                {{ completionStatusMessage }}
              </div>
            </div>
            
            <!-- Show completion date for ALL completed lessons -->
            <div v-if="isCompleted" class="completion-message">
              <span v-if="!isLateCompletion" class="text-success">✓ Lesson plan completed successfully!</span>
              <span v-else class="text-warning">⚠ Lesson plan completed late</span>
              <div class="completion-date mt-2">
                <strong>Completed on:</strong> {{ formatCompletionDate() }}
              </div>
            </div>
        </div>

        <!-- Notes Section - Show when completed -->
        <div v-if="isCompleted" class="notes-section">
            <div class="info-line">
              <strong>Notes & Comments:</strong>
              <span v-if="areCommentsRequired && hasUnsavedChanges" class="text-danger ml-2">(Required for late completion)</span>
              <span v-else-if="!hasUnsavedChanges" class="text-success ml-2">(Saved)</span>
              <span v-else class="text-muted ml-2">(Optional)</span>
            </div>
            <b-form-textarea
              v-model="notes"
              :placeholder="areCommentsRequired && hasUnsavedChanges ? 'Comments are required for completing lesson after the scheduled end date...' : 'Add your notes, observations, or comments about this lesson plan...'"
              rows="4"
              class="notes-textarea mt-2"
              :class="{ 'is-invalid': areCommentsRequired && hasUnsavedChanges && !(notes || '').trim() }"
              @blur="saveNotes"
            />
            <div v-if="areCommentsRequired && hasUnsavedChanges && !(notes || '').trim()" class="invalid-feedback">
              Comments are required when completing lessons after the scheduled end date.
            </div>
            
            <!-- Submit Button - Only show when there are unsaved changes -->
            <div v-if="shouldShowSaveButton" class="submit-section mt-4 text-center">
              <b-button
                variant="success"
                size="md"
                @click="saveComments"
                :disabled="!canSave || isSaving"
                class="submit-btn"
              >
                <b-spinner v-if="isSaving" small class="mr-2" />
                <feather-icon v-else icon="SaveIcon" class="mr-2" />
                {{ isSaving ? 'Saving...' : 'Save Changes' }}
              </b-button>
            </div>
            
            <!-- Status message when no changes to save -->
            <div v-else-if="isCompleted && !hasUnsavedChanges" class="text-center mt-3">
              <small class="text-muted">
                <feather-icon icon="CheckIcon" class="mr-1" size="14" />
                All changes saved
              </small>
            </div>
          </div>
        </div>

        <!-- Debug section (will be removed in production) -->
        <!-- <div v-if="showDebug" class="debug-section mt-4">
          <h6 class="text-warning mb-3">
            <feather-icon icon="InfoIcon" class="mr-1" />
            Debug Information
          </h6>
          <pre class="debug-data">{{ JSON.stringify(plannerData, null, 2) }}</pre>
        </div> -->
      </div>

      <div v-else class="text-center py-4">
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-muted mb-2" />
        <p class="text-muted mb-0">No planner data available.</p>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BButton, BFormTextarea, BSpinner } from "bootstrap-vue";

export default {
  name: "CalendarDetails",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormTextarea,
    BSpinner,
  },
  props: {
    plannerData: {
      type: Object,
      default: null,
    },
    topics: {
      type: Array,
      default: () => [],
    },
    subtopics: {
      type: Array,
      default: () => [],
    },
    showDebug: {
      type: Boolean,
      default: false,
    },
    metaClassName: {
      type: String,
      default: '',
    },
    hideDetails: {
      type: Boolean,
      default: null,
    },
    sessionID: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      isCompleted: false,
      notes: '', // Initialize as empty string, never null
      completionDate: null,
      isSaving: false, // Add loading state
    };
  },
  computed: {
    topicName() {
      if (this.plannerData?.meta_mainTopic) {
        return this.plannerData.meta_mainTopic;
      }
      if (this.plannerData?.topic) {
        return this.plannerData.topic;
      }
      if (this.plannerData?.metaTopicName) {
        return this.plannerData.metaTopicName;
      }
      if (this.plannerData?.mainTopic) {
        return this.plannerData.mainTopic;
      }
      return '';
    },
    subtopicName() {
      if (this.plannerData?.meta_subTopic) {
        return this.plannerData.meta_subTopic;
      }
      if (this.plannerData?.subTopic) {
        return this.plannerData.subTopic;
      }
      if (this.plannerData?.metaSubtopicName) {
        return this.plannerData.metaSubtopicName;
      }
      if (this.plannerData?.subtopic) {
        return this.plannerData.subtopic;
      }
      return '';
    },
    // Check if completion is within the date range
    isCompletionInRange() {
      if (!this.plannerData?.dtFrom || !this.plannerData?.dtTo) return true;
      
      const today = new Date();
      const startDate = new Date(this.plannerData.dtFrom);
      const endDate = new Date(this.plannerData.dtTo);
      
      return today >= startDate && today <= endDate;
    },
    // Check if completion is late (after end date)
    isLateCompletion() {
      if (!this.plannerData?.dtTo) return false;
      
      const today = new Date();
      const endDate = new Date(this.plannerData.dtTo);
      
      return today > endDate;
    },
    // Get completion status message
    completionStatusMessage() {
      if (!this.isCompleted) return '';
      
      if (this.isLateCompletion) {
        return 'Warning: Lesson marked as completed after the scheduled end date. Comments are required.';
      } else if (this.isCompletionInRange) {
        return 'Lesson completed within the scheduled date range.';
      } else {
        // Early completion - no warning needed
        return 'Lesson completed before the scheduled start date.';
      }
    },
    // Check if comments are required (only for late completion)
    areCommentsRequired() {
      return this.isCompleted && this.isLateCompletion;
    },
    // Check if save is allowed
    canSave() {
      if (!this.isCompleted) return true;
      if (!this.isLateCompletion) return true; // Allow early and in-range completion without comments
      
      // Add null safety for notes
      const currentNotes = this.notes || '';
      return currentNotes.trim().length > 0; // Comments required only for late completion
    },
    // Check if there are unsaved changes
    hasUnsavedChanges() {
      if (!this.plannerData) return false;
      
      // Check if completion status changed
      const originalCompleted = this.plannerData.isCompleted || false;
      if (this.isCompleted !== originalCompleted) return true;
      
      // Check if notes changed - add null safety
      const originalNotes = this.plannerData.note || '';
      const currentNotes = this.notes || '';
      if (currentNotes !== originalNotes) return true;
      
      return false;
    },
    // Show save button only when there are unsaved changes and save is allowed
    shouldShowSaveButton() {
      // Only show save button if there are unsaved changes
      if (!this.hasUnsavedChanges) return false;
      
      // If it's late completion and comments are required, check if comments exist
      const currentNotes = this.notes || '';
      if (this.isCompleted && this.isLateCompletion && !currentNotes.trim()) {
        return false; // Don't show save button until comments are provided
      }
      
      return this.canSave;
    },
    // Check if details should be hidden based on hideDetails prop
    shouldHideDetails() {
      
      return this.hideDetails === true;
    }
  },
  watch: {
    // Watcher removed - SOW data is now properly passed from Calendar.vue
    metaClassName: {
      handler(newVal, oldVal) {
      },
      immediate: true
    },
    hideDetails: {
      handler(newVal, oldVal) {
      },
      immediate: true
    }
  },
  mounted() {
    
    if (this.plannerData?.plannerDayWiseDetails) {
      this.plannerData.plannerDayWiseDetails.forEach((detail, index) => {
      });
    }

    // Load completion data from the planner data passed from Calendar.vue
    this.loadCompletionDataFromProps();
  },
  methods: {
    getPlannerHeading(index) {
      const headings = [
        'Warm-up',
        'Introduction to the topic', 
        'Main Activity',
        'Wrap-up',
        'Homework'
      ];
      return headings[index] || `Planner ${index + 1}`;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    },

    // Method to convert URLs to clickable links
    convertUrlsToLinks(text) {
      if (!text) return '';
      
      // First, check if the text already contains anchor tags
      if (text.includes('<a href=')) {
        // If it already has anchor tags, just return as is (for display)
        return text;
      }
      
      // Enhanced regex to match various URL patterns
      const urlRegex = /(@?https?:\/\/[^\s]+|@?www\.[^\s]+|@?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]*)/gi;
      
      return text.replace(urlRegex, (match) => {
        // Remove @ if present and create the actual URL
        const actualUrl = match.startsWith('@') ? match.substring(1) : match;
        const displayText = match;
        
        // Ensure URL has protocol
        const finalUrl = actualUrl.startsWith('http') ? actualUrl : `https://${actualUrl}`;
        
        return `<a href="${finalUrl}" target="_blank" rel="noopener noreferrer" class="clickable-link">${displayText}</a>`;
      });
    },

    // Method to handle link clicks
    handleLinkClick(event) {
      // Prevent default behavior if it's a link
      if (event.target.tagName === 'A') {
        event.preventDefault();
        const url = event.target.href;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },

    // Method to check if content contains URLs
    hasUrls(text) {
      if (!text) return false;
      const urlRegex = /(@?https?:\/\/[^\s]+|@?www\.[^\s]+|@?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]*)/gi;
      return urlRegex.test(text);
    },
    formatCompletionDate() {
      // Use the actual completion date from plannerData if available
      let dateToFormat = this.completionDate;
      
      // Fallback to plannerData.completedOn if completionDate is not set
      if (!dateToFormat && this.plannerData?.completedOn) {
        dateToFormat = this.plannerData.completedOn;
      }
      
      // If still no date, use current date (for new completions)
      if (!dateToFormat) {
        dateToFormat = new Date().toISOString();
      }
      
      const date = new Date(dateToFormat);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    onCompletionChange() {
      if (this.isCompleted) {
        // Only set completion date if it's not already set (new completion)
        if (!this.completionDate && !this.plannerData?.completedOn) {
          this.completionDate = new Date().toISOString();
        }
        
        // Only auto-save if it's not late completion (late completion requires comments)
        if (!this.isLateCompletion) {
          this.saveLessonData();
        }
      } else {
        // When unchecking, only clear if it was a new completion (not from API)
        if (!this.plannerData?.completedOn) {
          this.completionDate = null;
        }
        this.saveLessonData(); // Always save when unchecking
      }
    },
    saveNotes() {
      // Don't auto-save anymore - let the user click the save button
      // The save button will appear automatically due to hasUnsavedChanges computed property
    },
    saveLessonData() {
      if (!this.plannerData?.id) return;
      
      // Call the API to mark as complete
      this.markAsCompleteAPI();
    },
    
    async markAsCompleteAPI() {
      this.isSaving = true; // Set loading state
      try {
        const apiUrl = this.$store.state.domain + 'PlannerDayWise/MarkAsComplete';
        
        const campusID =
          this.plannerData?.campusID || this.$store.state.userData.cId;
        const sessionID =
          this.sessionID != null
            ? this.sessionID
            : this.plannerData?.sessionID || 0;

        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          campusID: String(campusID),
          id: this.plannerData.id,
          isCompleted: this.isCompleted,
          note: this.notes || '',
          userID: this.$store.state.userData.userID,
          sessionID: String(sessionID),
        });
        
        const fullUrl = `${apiUrl}?${params.toString()}`;
        
        
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.$store.state.userData.token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          
          // Update the local planner data with the new completion status
          if (this.plannerData) {
            this.plannerData.isCompleted = this.isCompleted;
            this.plannerData.note = this.notes || '';
            this.plannerData.completedOn = this.isCompleted ? (this.completionDate || new Date().toISOString()) : null;
          }
          
          // Emit event to parent to refresh calendar data
          this.$emit('data-updated', {
            plannerId: this.plannerData.id,
            isCompleted: this.isCompleted,
            note: this.notes || '',
            completedOn: this.isCompleted ? (this.completionDate || new Date().toISOString()) : null
          });
          
          // Show success toast notification
          this.$bvToast.toast("Lesson plan status updated successfully", {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
            autoHideDelay: 3000
          });
        } else {
          console.error("API call failed:", response.status, response.statusText);
          throw new Error(`API call failed: ${response.status} ${response.statusText}`);
        }
        
      } catch (error) {
        console.error("Error calling Mark as Complete API:", error);
        
        // Show error toast notification
        this.$bvToast.toast("Failed to update lesson plan status. Please try again.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.isSaving = false; // Reset loading state
      }
    },
    
    async loadCompletionDataFromProps() {
      if (!this.plannerData?.id) return;
      
      try {
        // Load completion data from the plannerData passed from Calendar.vue
        // The LoadSelected API already includes isCompleted, completedOn, and note
        
        if (this.plannerData.isCompleted !== undefined) {
          this.isCompleted = this.plannerData.isCompleted;
        }
        if (this.plannerData.note !== undefined) {
          this.notes = this.plannerData.note || ''; // Ensure never null
        }
        if (this.plannerData.completedOn !== undefined) {
          this.completionDate = this.plannerData.completedOn;
        }
        
        
      } catch (error) {
        console.error("Error loading completion data from props:", error);
        // Set defaults if there's an error - ensure notes is never null
        this.isCompleted = false;
        this.notes = ''; // Always string, never null
        this.completionDate = null;
      }
    },
    
    viewAllPlannerDetails() {
      
      // Create the HTML content for the print view
      const printContent = this.generatePrintableHTML(null); // Pass null for all planners view
      
      // Open new window/tab
      const newWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      
      if (newWindow) {
        newWindow.document.write(printContent);
        newWindow.document.close();
        
        // Add print functionality after the content is loaded
        newWindow.onload = function() {
          // Add print button functionality
          const printBtn = newWindow.document.getElementById('printButton');
          if (printBtn) {
            printBtn.addEventListener('click', function() {
              newWindow.print();
            });
          }
          
          // Add click event handling for links
          const links = newWindow.document.querySelectorAll('.clickable-link');
          links.forEach(link => {
            link.addEventListener('click', function(e) {
              e.preventDefault();
              const url = this.href;
              window.open(url, '_blank', 'noopener,noreferrer');
            });
          });
        };
      } else {
        alert('Pop-up blocked! Please allow pop-ups for this site to view the full planner.');
      }
    },
    
    generatePrintableHTML(detail) {
      const topicName = this.topicName || 'Unknown Topic';
      const subtopicName = this.subtopicName || 'Unknown Subtopic';
      const dateRange = this.formatDateRange();
      const gradeDisplay = this.metaClassName || this.plannerData?.grade || '3';
      
      
      // Generate subtopic list HTML for the top table
      let subtopicListHTML = '';
      if (subtopicName && subtopicName !== 'Unknown Subtopic') {
        subtopicListHTML = `● ${subtopicName}`;
      } else {
        // Fallback to lesson organisations if subtopic not available
        if (detail && detail.lessonOrganisation) {
          subtopicListHTML = `● ${detail.lessonOrganisation}`;
        }
      }
      
      // Generate learning outcomes from actual lesson details
      let overallOutcome = '';
      let specificOutcome = '';
      let pedagogy = '';
      
      // Extract real data from planner details
      if (detail) {
        const activity = detail.activity || 'No activity specified';
        const resource = detail.resource || 'No resource specified';
        const assessmentStrategy = detail.assessmentStrategy || 'Assessment to be determined';
        
        // Build outcomes from actual lesson organizations
        const lessonOrg = detail.lessonOrganisation || 'No lesson organization specified';
        if (lessonOrg && lessonOrg !== 'No lesson organization specified') {
          overallOutcome = `Students will engage in: ${lessonOrg}.`;
          specificOutcome = `Complete ${lessonOrg} successfully`;
        }
        
        // Build pedagogy from actual activities and resources
        if (activity !== 'No activity specified' || resource !== 'No resource specified') {
          const methods = [];
          if (activity !== 'No activity specified') methods.push(activity);
          if (resource !== 'No resource specified') methods.push(`Using: ${resource}`);
          pedagogy = methods.join('. ');
        }
      } else {
        // For all planners view, aggregate data from all planners
        if (this.plannerData.plannerDayWiseDetails && this.plannerData.plannerDayWiseDetails.length > 0) {
          const allActivities = this.plannerData.plannerDayWiseDetails
            .map(d => d.activity)
            .filter(a => a && a.trim())
            .join(', ');
          
          const allResources = this.plannerData.plannerDayWiseDetails
            .map(d => d.resource)
            .filter(r => r && r.trim())
            .join(', ');
          
          const allOrganizations = this.plannerData.plannerDayWiseDetails
            .map(d => d.lessonOrganisation)
            .filter(o => o && o.trim())
            .join(', ');
          
          if (allOrganizations) {
            overallOutcome = `Students will engage in: ${allOrganizations}.`;
            specificOutcome = `Complete all planned activities successfully`;
          }
          
          if (allActivities || allResources) {
            const methods = [];
            if (allActivities) methods.push(allActivities);
            if (allResources) methods.push(`Using: ${allResources}`);
            pedagogy = methods.join('. ');
          }
        }
      }
      
      // Fallback if no real data available
      if (!overallOutcome) {
        overallOutcome = 'Students will gain understanding of the lesson content through structured activities.';
      }
      if (!specificOutcome) {
        specificOutcome = 'Apply learned concepts in practical scenarios.';
      }
      if (!pedagogy) {
        pedagogy = 'Interactive teaching methods and hands-on learning activities.';
      }
      
      // Generate planner rows HTML for the main table
      let plannerRowsHTML = '';
      
      if (detail) {
        // Single planner view
        const lessonOrg = detail.lessonOrganisation || 'Lesson Activity';
        const activity = detail.activity || 'No activity specified';
        const resource = detail.resource || 'No resource specified';
        const assessmentStrategy = detail.assessmentStrategy || 'Assessment to be determined';
        
        plannerRowsHTML += `
          <tr>
            <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-weight: bold; width: 25%;">
              ${this.convertUrlsToLinks(lessonOrg)}
            </td>
            <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
              ${this.convertUrlsToLinks(activity)}
            </td>
            <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
              ${this.convertUrlsToLinks(resource)}
            </td>
            <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
              ${this.convertUrlsToLinks(assessmentStrategy)}
            </td>
          </tr>
        `;
      } else {
        // All planners view
        if (this.plannerData.plannerDayWiseDetails && this.plannerData.plannerDayWiseDetails.length > 0) {
          this.plannerData.plannerDayWiseDetails.forEach((plannerDetail, index) => {
            const lessonOrg = plannerDetail.lessonOrganisation || 'Lesson Activity';
            const activity = plannerDetail.activity || 'No activity specified';
            const resource = plannerDetail.resource || 'No resource specified';
            const assessmentStrategy = plannerDetail.assessmentStrategy || 'Assessment to be determined';
            
            plannerRowsHTML += `
              <tr>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-weight: bold; width: 25%; background-color: #f8f9fa;">
                  ${this.convertUrlsToLinks(lessonOrg)}
                </td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
                  ${this.convertUrlsToLinks(activity)}
                </td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
                  ${this.convertUrlsToLinks(resource)}
                </td>
                <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
                  ${this.convertUrlsToLinks(assessmentStrategy)}
                </td>
              </tr>
            `;
          });
        }
      }

      // If no planner details, add a placeholder row
      if (!detail && (!this.plannerData.plannerDayWiseDetails || this.plannerData.plannerDayWiseDetails.length === 0)) {
        plannerRowsHTML = `
          <tr>
            <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-weight: bold; width: 25%;">
              No lesson details available
            </td>
            <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
              -
            </td>
            <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
              -
            </td>
            <td style="padding: 8px; border: 1px solid #000; vertical-align: top; font-size: 11px; width: 25%;">
              -
            </td>
          </tr>
        `;
      }
      
      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Lesson Planner - ${topicName}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              line-height: 1.3;
              color: #000;
              background-color: #fff;
              padding: 15px;
              font-size: 12px;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 20px;
              padding: 15px;
              background: linear-gradient(135deg, #7367f0 0%, #9c88ff 100%);
              color: white;
              border-radius: 8px;
            }
            
            .print-header h1 {
              font-size: 24px;
              margin-bottom: 8px;
              font-weight: bold;
            }
            
            .print-header p {
              font-size: 14px;
              opacity: 0.9;
            }
            
            .lesson-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              border: 2px solid #000;
              font-size: 11px;
              table-layout: fixed;
            }
            
            .lesson-table td {
              padding: 8px;
              border-left: 1px solid #000;
              border-right: 1px solid #000;
              vertical-align: top;
              word-wrap: break-word;
            }
            
            .lesson-table th {
              padding: 8px;
              border-left: 1px solid #000;
              border-right: 1px solid #000;
              font-weight: bold;
              text-align: center;
              font-size: 11px;
              background-color: #fff;
            }
            
            /* Define column structure using colgroup equivalent */
            .lesson-table {
              table-layout: fixed;
              width: 100%;
            }
            
            /* First 2 rows: 3 equal columns (33.33% each) */
            .lesson-table tr:nth-child(1) td,
            .lesson-table tr:nth-child(2) td {
              width: 33.33% !important;
              max-width: 33.33% !important;
            }
            
            /* Rows 3-4: Lesson Objectives section */
            .lesson-table tr:nth-child(3) td:nth-child(1),
            .lesson-table tr:nth-child(4) td:nth-child(1) {
              width: 66.67% !important;
              max-width: 66.67% !important;
            }
            
            .lesson-table tr:nth-child(3) td:nth-child(2),
            .lesson-table tr:nth-child(4) td:nth-child(2) {
              width: 33.33% !important;
              max-width: 33.33% !important;
            }
            
            /* Row 5 and beyond: 4 equal columns (25% each) */
            .lesson-table tr:nth-child(n+5) td:nth-child(1),
            .lesson-table tr:nth-child(n+5) td:nth-child(2),
            .lesson-table tr:nth-child(n+5) td:nth-child(3),
            .lesson-table tr:nth-child(n+5) td:nth-child(4) {
              width: 25% !important;
              max-width: 25% !important;
              min-width: 25% !important;
            }
            
            .print-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
            }
            
            .print-btn {
              background: #28a745;
              color: white;
              border: none;
              padding: 10px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 12px;
              font-weight: bold;
              box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
              transition: all 0.3s ease;
            }
            
            .print-btn:hover {
              background: #218838;
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
            }
            
            @media print {
              .print-controls {
                display: none !important;
              }
              
              body {
                padding: 8px;
                font-size: 11px;
              }
              
              .print-header {
                background: #7367f0 !important;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
            }
            
            /* Link styles for print view */
            .clickable-link {
              color: #007bff !important;
              text-decoration: underline !important;
              font-weight: 500;
              cursor: pointer;
              background-color: rgba(0, 123, 255, 0.1);
              padding: 1px 2px;
              border-radius: 2px;
              transition: all 0.2s ease;
            }
            
            .clickable-link:hover {
              color: #0056b3 !important;
              text-decoration: none !important;
              background-color: rgba(0, 123, 255, 0.2);
            }
            
            @media print {
              .clickable-link {
                color: #007bff !important;
                text-decoration: underline !important;
                background-color: transparent !important;
                padding: 0 !important;
              }
            }
          </style>
        </head>
        <body>
          <!-- Print Controls -->
          <div class="print-controls">
            <button id="printButton" class="print-btn">
              🖨️ Print Planner
            </button>
          </div>
          
          <!-- Header -->
          <div class="print-header">
            <h1>Lesson Planner</h1>
            <p>${topicName} - ${subtopicName}</p>
          </div>
          
          <!-- Single Continuous Table -->
          <table class="lesson-table">
            <!-- Define base 4-column structure -->
            <colgroup>
              <col style="width: 25%;">
              <col style="width: 25%;">
              <col style="width: 25%;">
              <col style="width: 25%;">
            </colgroup>
            
            <!-- Header Row - 3 equal columns (33% each) -->
            <tr>
              <td style="font-weight: bold; text-align: left; width: 33.33%; padding: 8px; border: 1px solid #000;"><strong>Grade: ${gradeDisplay}</strong></td>
              <td style="font-weight: bold; text-align: left; width: 33.33%; padding: 8px; border: 1px solid #000;"><strong>Subject:</strong> ${this.plannerData.subject || 'Mathematics'}</td>
              <td style="font-weight: bold; text-align: left; width: 33.33%; padding: 8px; border: 1px solid #000;"><strong>Topic:</strong> ${topicName}</td>
            </tr>
            
            <!-- Subtopic Row - 3 equal columns (33% each) -->
            <tr>
              <td style="vertical-align: top; width: 33.33%; padding: 8px; border: 1px solid #000;">
                <strong>Subtopic:</strong><br>
                ${subtopicListHTML || `● ${subtopicName}`}
              </td>
              <td style="width: 33.33%; padding: 8px; border: 1px solid #000;"><strong>Duration:</strong><br>${this.plannerData.duration || 'Not specified'}</td>
              <td style="width: 33.33%; padding: 8px; border: 1px solid #000;"><strong>Date/Day:</strong><br>${dateRange}</td>
            </tr>
            
            <!-- Lesson Objectives Header Row - 2 equal columns (50% each) -->
            <tr>
              <td colspan="2" style="font-weight: bold; text-align: center; background-color: #fff; border: 1px solid #000; padding: 8px;">
                <strong>Lesson Objectives</strong>
              </td>
              <td colspan="2" style="font-weight: bold; text-align: center; background-color: #fff; border: 1px solid #000; padding: 8px;">
                <strong>Pedagogy</strong>
              </td>
            </tr>
            
            <!-- Lesson Objectives Content Row - 2 equal columns (50% each) -->
            <tr>
              <td colspan="2" style="vertical-align: top; border: 1px solid #000; padding: 8px;">
                <strong>Overall Learning Outcome:</strong><br>
                ${overallOutcome}<br><br>
                <strong>Specific Learning Outcome:</strong><br>
                ${specificOutcome}
              </td>
              <td colspan="2" style="vertical-align: top; border: 1px solid #000; padding: 8px;">
                ${pedagogy}
              </td>
            </tr>
            
            <!-- Main Table Headers - 4 equal columns (25% each) -->
            <tr>
              <td style="font-weight: bold; text-align: center; background-color: #fff; border: 1px solid #000; width: 25%; padding: 8px;"><strong>Lesson Organisation</strong></td>
              <td style="font-weight: bold; text-align: center; background-color: #fff; border: 1px solid #000; width: 25%; padding: 8px;"><strong>Activity</strong></td>
              <td style="font-weight: bold; text-align: center; background-color: #fff; border: 1px solid #000; width: 25%; padding: 8px;"><strong>Resource</strong></td>
              <td style="font-weight: bold; text-align: center; background-color: #fff; border: 1px solid #000; width: 25%; padding: 8px;"><strong>Assessment strategy with Bloom's Taxonomy alignment</strong></td>
            </tr>
            
            ${plannerRowsHTML}
          </table>
          
          <!-- Footer -->
          <div style="text-align: center; margin-top: 30px; padding: 15px; border-top: 1px solid #ccc; color: #666;">
            <p style="font-size: 10px;">Generated on ${new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>
        </body>
        </html>
      `;
    },
    
    formatDateRange() {
      if (this.plannerData.dtFrom && this.plannerData.dtTo) {
        const fromDate = this.formatDate(this.plannerData.dtFrom);
        const toDate = this.formatDate(this.plannerData.dtTo);
        return `${fromDate} - ${toDate}`;
      }
      return 'Date not specified';
    },
    saveComments() {
      // This method will submit the lesson plan to the backend
      this.markAsCompleteAPI();
    },
    
    previewSow() {
      
      if (this.plannerData.sow) {
        window.open(this.plannerData.sow, '_blank', 'noopener,noreferrer');
      } else if (this.plannerData.plannerDayWise && this.plannerData.plannerDayWise.sow) {
        window.open(this.plannerData.plannerDayWise.sow, '_blank', 'noopener,noreferrer');
      } else {
      }
    },
    
    previewUrdu() {
      
      if (this.plannerData.urdu) {
        window.open(this.plannerData.urdu, '_blank', 'noopener,noreferrer');
      } else if (this.plannerData.plannerDayWise && this.plannerData.plannerDayWise.urdu) {
        window.open(this.plannerData.plannerDayWise.urdu, '_blank', 'noopener,noreferrer');
      } else {
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.calendar-details {
  .basic-info {
    padding: 1.5rem;
    background: #fafbfc;
    border-bottom: 1px solid #e9ecef;
    border-radius: 8px 8px 0 0;
  }

  .section-title {
    color: #2c3e50;
    font-weight: 700;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    border-bottom: 2px solid #7367f0;
    padding-bottom: 0.5rem;
    display: inline-block;
  }

  .info-line {
    margin-bottom: 0.75rem;
    line-height: 1.6;
    padding: 0.5rem 0;
    
    strong {
      color: #2c3e50;
      font-weight: 600;
      margin-right: 0.75rem;
      min-width: 120px;
      display: inline-block;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  .activities-section {
    padding: 1.5rem;
    background: #ffffff;
    border-bottom: 1px solid #e9ecef;

    .activity-count {
      font-weight: 500;
      color: #7367f0;
      font-size: 0.9rem;
      background: #f3f2ff;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      margin-left: 0.5rem;
    }

    .activity-item {
      padding: 1.25rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border-radius: 8px;
      border: 1px solid #e9ecef;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        transform: translateY(-2px);
      }

      .activity-title {
        color: #7367f0;
        font-weight: 700;
        margin-bottom: 1rem;
        font-size: 1rem;
        padding: 0.5rem 0;
        border-bottom: 1px solid #e9ecef;
      }
    }

    .no-activities {
      padding: 2rem;
      text-align: center;
      background: #f8f9fa;
      border-radius: 8px;
      border: 2px dashed #dee2e6;
    }
  }

  .completion-section {
    padding: 1.5rem;
    background: #fafbfc;
    border-radius: 0 0 8px 8px;

    .completion-checkbox {
      .custom-control {
        display: flex;
        align-items: center;
        padding-left: 0;
        
        .custom-control-input {
          position: relative;
          left: 0;
          z-index: 1;
          width: 1.25rem;
          height: 1.25rem;
          opacity: 1;
          margin-right: 0.75rem;
          
          &:checked {
            background-color: #7367f0;
            border-color: #7367f0;
          }
          
          &:focus {
            box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
          }
        }
        
        .custom-control-label {
          font-weight: 600;
          color: #2c3e50;
          font-size: 1rem;
          line-height: 1.25rem;
          margin-bottom: 0;
          padding-left: 0;
          cursor: pointer;
          
          &::before,
          &::after {
            display: none;
          }
        }
      }
      
      // Red background for out-of-range completion
      &.out-of-range {
        border-left: 4px solid #ff6b6b;
        background: #fff5f5;
        padding: 0.75rem;
        margin: 0.5rem 0;
        border-radius: 4px;
        
        .custom-control-label {
          color: #d63031;
          font-weight: 600;
        }
      }
    }

    .completion-status-message {
      padding: 0.75rem 1rem;
      border-radius: 6px;
      margin-top: 0.75rem;
      font-weight: 500;
      
      &:has(.text-success) {
        background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
        border: 1px solid #b8dacc;
        color: #155724;
      }
      
      &:has(.text-danger) {
        background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
        border: 1px solid #f1b0b7;
        color: #721c24;
      }
      
      .feather-icon {
        margin-right: 0.5rem;
        width: 16px;
        height: 16px;
      }
    }

    .completion-message {
      margin-top: 0.75rem;
      font-size: 0.95rem;
      font-weight: 600;
      padding: 0.75rem 1rem;
      background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
      border: 1px solid #b8dacc;
      border-radius: 6px;
      color: #155724;
      
      // Add styling for warning message
      &:has(.text-warning) {
        background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
        border: 1px solid #ffeaa7;
        color: #856404;
      }
    }

    .completion-date {
      font-size: 0.85rem;
      color: #6c757d;
      margin-top: 0.5rem;
    }

    .notes-section {
      margin-top: 1.5rem;
      padding: 1rem;
      background: #ffffff;
      border-radius: 8px;
      border: 1px solid #e9ecef;

      .notes-textarea {
        border: 2px solid #e9ecef;
        border-radius: 6px;
        padding: 1rem;
        font-size: 0.95rem;
        resize: vertical;
        transition: border-color 0.3s ease;
        
        &:focus {
          border-color: #7367f0;
          box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.15);
        }
        
        &.is-invalid {
          border-color: #dc3545;
          
          &:focus {
            border-color: #dc3545;
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.15);
          }
        }
      }
      
      .invalid-feedback {
        display: block;
        font-size: 0.875rem;
        color: #dc3545;
        margin-top: 0.5rem;
      }

      .notes-help {
        text-align: right;
        font-size: 0.85rem;
        color: #6c757d;
        font-style: italic;
      }
    }
  }
}

// Enhanced card styling
.card {
  border-radius: 12px !important;
  box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
  border: 1px solid #e9ecef !important;
  overflow: hidden !important;
  
  .card-header {
    background: linear-gradient(135deg, #7367f0 0%, #9c88ff 100%) !important;
    color: #ffffff !important;
    border-bottom: none !important;
    padding: 1.25rem 1.5rem !important;
    
    h5 {
      color: #ffffff !important;
      font-weight: 700 !important;
      margin: 0 !important;
    }
  }
}

// Enhanced button styling
.btn-outline-secondary {
  border-radius: 8px !important;
  font-weight: 600 !important;
  border: 2px solid #ffffff !important;
  background: rgba(255, 255, 255, 0.1) !important;
  color: #ffffff !important;
  padding: 0.5rem 1.25rem !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2) !important;
    border-color: #ffffff !important;
    color: #ffffff !important;
    transform: translateY(-1px) !important;
  }
}

// View Details button styling
.view-details-btn {
  border-radius: 6px !important;
  font-weight: 500 !important;
  padding: 0.375rem 0.75rem !important;
  font-size: 0.875rem !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3) !important;
  }
}

// Action buttons container styling
.action-buttons {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  flex-wrap: wrap !important;
}

// SOW preview button styling
.btn-outline-success {
  border-radius: 6px !important;
  font-weight: 500 !important;
  padding: 0.375rem 0.75rem !important;
  font-size: 0.875rem !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3) !important;
  }
}

// Submit button styling
.submit-btn {
  border-radius: 8px !important;
  font-weight: 600 !important;
  padding: 0.75rem 2rem !important;
  font-size: 1rem !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 12px rgba(40, 199, 111, 0.3) !important;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(40, 199, 111, 0.4) !important;
  }
  
  &:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
  }
}

// Submit section styling
.submit-section {
  border-top: 1px solid #e9ecef;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
}

// Saved status message styling
.text-center .text-muted {
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  border-radius: 20px;
  border: 1px solid #e9ecef;
  font-weight: 500;
  
  .feather-icon {
    color: #28a745;
  }
}

// Enhanced typography
h5, h6 {
  color: #2c3e50;
  font-weight: 700;
}

.text-muted {
  color: #6c757d !important;
}

.text-success {
  color: #28a745 !important;
}

// Custom scrollbar for textarea
.notes-textarea::-webkit-scrollbar {
  width: 6px;
}

.notes-textarea::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.notes-textarea::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.notes-textarea::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

// Clickable link styles
.clickable-link {
  color: #007bff !important;
  text-decoration: underline !important;
  font-weight: 500;
  cursor: pointer;
  background-color: rgba(0, 123, 255, 0.1);
  padding: 1px 2px;
  border-radius: 2px;
  transition: all 0.2s ease;
}

.clickable-link:hover {
  color: #0056b3 !important;
  text-decoration: none !important;
  background-color: rgba(0, 123, 255, 0.2);
}

// Print link styles
@media print {
  .clickable-link {
    color: #007bff !important;
    text-decoration: underline !important;
    background-color: transparent !important;
    padding: 0 !important;
  }
  
  .clickable-link:hover {
    color: #007bff !important;
    text-decoration: underline !important;
    background-color: transparent !important;
  }
}
</style> 